import { ArrowRightLeft, Pencil, Plus, Trash2 } from "lucide-mui";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Link from "@mui/material/Link";
import MenuItem from "@mui/material/MenuItem";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import toast from "react-hot-toast";
import { useOrgTenants, useTenants } from "../../hooks/resources.ts";
import NoSelectionPrompt from "../../components/NoSelectionPrompt.tsx";
import { api, type OrgTenant, TenantCounts } from "../../api/client.ts";
import { keys } from "../../api/queryKeys.ts";
import FormattedDate from "../../components/FormattedDate";
import { TenantCell } from "../../components/IdentityCell.tsx";
import OwnershipChip from "../../components/OwnershipChip.tsx";
import RowActions from "../../components/RowActions.tsx";
import TenantDialog from "../../components/TenantDialog.tsx";
import EmptyTableRow from "../../components/table/EmptyTableRow.tsx";
import FilterBar from "../../components/filters/FilterBar.tsx";
import type { FilterSpec } from "../../components/filters/specs.ts";
import { useFilters, useLocalFilterState } from "../../components/filters/useFilters.ts";
import ScrollTable from "../../components/table/ScrollTable.tsx";
import TableCard from "../../components/table/TableCard.tsx";
import { ORG_CRM_READ_SCOPE } from "../../auth/orgRoutes.ts";
import { userHasOrgScope } from "../../auth/scopes";
import { useAuth } from "../../contexts/AuthContext.tsx";
import { useOrganization } from "../../contexts/OrganizationContext.tsx";
import { formatNumber } from "../../utils/format.ts";
import TenantDataDialog from "../settings/TenantDataDialog.tsx";
import PageHeader from "../../components/PageHeader.tsx";

// Search only.
const NO_FILTERS: FilterSpec[] = [];

function TenantsPage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { selectedOrgId, currentOrg } = useOrganization();
  const isOrgAdmin = userHasOrgScope(user, selectedOrgId, "org:manage_members");
  const canTransfer = userHasOrgScope(user, selectedOrgId, "org:write");
  // CRM counts and the per-object drill-down are served only to CRM readers.
  const canReadCrm = userHasOrgScope(user, selectedOrgId, ORG_CRM_READ_SCOPE);

  const [dataDialogState, setDataDialogState] = useState<{
    tenantId: string;
    tenantName: string;
    objectType: string;
  } | null>(null);
  const [tenantDialogOpen, setTenantDialogOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const filterState = useLocalFilterState();
  const filters = useFilters(NO_FILTERS, { state: filterState, search: { param: "q", placeholder: "Search tenants" } });

  const tenants = useTenants(selectedOrgId);

  // Ownership: which org owns each tenant and who else it is shared with.
  const orgTenants = useOrgTenants(selectedOrgId);
  const linkByTenant = new Map<string, OrgTenant>(
    (orgTenants.data ?? []).map((ot) => [ot.tenant_id, ot]),
  );

  const [transferring, setTransferring] = useState<OrgTenant | null>(null);
  const [newOwnerId, setNewOwnerId] = useState("");
  const closeTransfer = () => {
    setTransferring(null);
    setNewOwnerId("");
  };
  const transferMutation = useMutation({
    mutationFn: () =>
      api.setTenantPrimaryOrganization(selectedOrgId!, transferring!.tenant_id, newOwnerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.organizationTenant.all });
      toast.success("Ownership transferred");
      closeTransfer();
    },
  });

  const tenantStats = useQuery({
    queryKey: keys.tenant.stats(selectedOrgId ?? ""),
    queryFn: () => api.getTenantStats({ organization_id: selectedOrgId! }),
    enabled: !!selectedOrgId && canReadCrm,
  });

  const statsMap = new Map<string, TenantCounts>();
  if (tenantStats.data) {
    for (const entry of tenantStats.data) {
      statsMap.set(entry.tenant_id, entry.counts);
    }
  }

  const deleteMutation = useMutation({
    mutationFn: api.deleteTenant,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.tenant.all });
      queryClient.invalidateQueries({ queryKey: keys.organizationTenant.byOrg(selectedOrgId!) });
      toast.success("Tenant deleted");
    },
  });

  const openAddDialog = () => {
    setEditingTenant(null);
    setTenantDialogOpen(true);
  };

  const openEditDialog = (tenant: { id: string; name: string }) => {
    setEditingTenant(tenant);
    setTenantDialogOpen(true);
  };

  const closeDialog = () => {
    setTenantDialogOpen(false);
    setEditingTenant(null);
  };

  if (!selectedOrgId) {
    return (
      <Box>
        <PageHeader title="Tenants" />
        <NoSelectionPrompt kind="org" />
      </Box>
    );
  }

  const all = tenants.data ?? [];
  const query = (filters.get("q") ?? "").trim().toLowerCase();
  const visible = query ? all.filter((t) => t.name.toLowerCase().includes(query)) : all;
  const orgName = currentOrg?.name ?? "this organization";
  const tenantsWord = all.length === 1 ? "tenant" : "tenants";
  const count =
    tenants.data &&
    (query
      ? `${formatNumber(visible.length)} of ${formatNumber(all.length)} ${tenantsWord}`
      : `${formatNumber(all.length)} ${tenantsWord}`);
  const showActions = isOrgAdmin || canTransfer;
  const columnCount = 3 + (canReadCrm ? 4 : 0) + (showActions ? 1 : 0);

  return (
    <>
      <PageHeader
        title="Tenants"
        description={`The client workspaces ${orgName} works in`}
        actions={
          isOrgAdmin && (
            <Button variant="contained" startIcon={<Plus />} onClick={openAddDialog}>
              Add tenant
            </Button>
          )
        }
      />
      <TableCard
        loading={tenants.isLoading}
        toolbar={<FilterBar filters={filters} count={count} />}
      >
        <ScrollTable>
          <TableHead>
            <TableRow>
              <TableCell>Tenant</TableCell>
              <TableCell>Owner</TableCell>
              <TableCell>Created</TableCell>
              {canReadCrm && (
                <>
                  <TableCell align="right">Contacts</TableCell>
                  <TableCell align="right">Companies</TableCell>
                  <TableCell align="right">Deals</TableCell>
                  <TableCell align="right">Activities</TableCell>
                </>
              )}
              {showActions && <TableCell padding="checkbox" />}
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((t) => {
              const counts = statsMap.get(t.id);
              const link = linkByTenant.get(t.id);
              return (
                <TableRow key={t.id} hover>
                  <TableCell>
                    <TenantCell name={t.name} logoUrl={t.logo_url} />
                  </TableCell>
                  <TableCell>{link && <OwnershipChip link={link} />}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap", color: "text.secondary" }}>
                    <FormattedDate value={t.created_at} />
                  </TableCell>
                  {canReadCrm &&
                    (["contacts", "companies", "deals", "activities"] as const).map((type) => {
                      const n = counts?.[type] ?? 0;
                      return (
                        <TableCell key={type} align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                          {n > 0 ? (
                            <Link
                              component="button"
                              onClick={() =>
                                setDataDialogState({
                                  tenantId: t.id,
                                  tenantName: t.name,
                                  objectType: type,
                                })
                              }
                            >
                              {formatNumber(n)}
                            </Link>
                          ) : (
                            0
                          )}
                        </TableCell>
                      );
                    })}
                  {showActions && (
                    <TableCell padding="checkbox" align="right">
                      <RowActions
                        label={t.name}
                        actions={[
                          {
                            label: "Edit",
                            icon: <Pencil fontSize="small" />,
                            hidden: !isOrgAdmin,
                            onClick: () => openEditDialog({ id: t.id, name: t.name }),
                          },
                          {
                            label: "Transfer ownership",
                            icon: <ArrowRightLeft fontSize="small" />,
                            hidden: !(canTransfer && link?.is_primary && link.shared_with.length > 0),
                            onClick: () => link && setTransferring(link),
                          },
                          {
                            label: "Delete",
                            icon: <Trash2 fontSize="small" />,
                            destructive: true,
                            hidden: !isOrgAdmin,
                            onClick: () => deleteMutation.mutateAsync(t.id),
                            confirm: {
                              title: "Delete tenant",
                              body: (
                                <>
                                  Delete tenant <strong>{t.name}</strong>?
                                </>
                              ),
                            },
                          },
                        ]}
                      />
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
            {visible.length === 0 && (
              <EmptyTableRow colSpan={columnCount}>
                {all.length === 0 ? "No tenants in this organization" : "No tenants match your search"}
              </EmptyTableRow>
            )}
          </TableBody>
        </ScrollTable>
      </TableCard>
      {dataDialogState && (
        <TenantDataDialog
          tenantId={dataDialogState.tenantId}
          tenantName={dataDialogState.tenantName}
          objectType={dataDialogState.objectType}
          onClose={() => setDataDialogState(null)}
        />
      )}
      <Dialog open={!!transferring} onClose={closeTransfer} maxWidth="sm" fullWidth>
        <DialogTitle>Transfer ownership: {transferring?.tenant_name}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            The owning organization receives this tenant's prospect orders and pays for
            their agent runs. {currentOrg?.name ?? "This organization"} keeps access, but
            only the new owner's admins can transfer it again.
          </DialogContentText>
          <TextField
            select
            fullWidth
            label="New owner"
            value={newOwnerId}
            onChange={(e) => setNewOwnerId(e.target.value)}
          >
            {(transferring?.shared_with ?? []).map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {o.name}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeTransfer}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => transferMutation.mutate()}
            disabled={!newOwnerId}
            loading={transferMutation.isPending}
          >
            Transfer
          </Button>
        </DialogActions>
      </Dialog>
      {tenantDialogOpen && (
        <TenantDialog
          onClose={closeDialog}
          organizationId={selectedOrgId}
          tenant={editingTenant ?? undefined}
        />
      )}
    </>
  );
}

export default TenantsPage;
